import { useImagePreview } from '@/hooks/useImagePreview';
import type { ModelSolid } from '@/types/project.types';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ModelPreviewProps {
  modelId: string | null;
  /** Which solid of the STEP document to show: the part itself or the stock it comes from. */
  solid?: ModelSolid;
  alt?: string;
  disableGif?: boolean;
}

export const ModelPreview: FC<ModelPreviewProps> = ({
  modelId,
  solid = 'model',
  alt = 'Model preview',
  disableGif = false,
}) => {
  const { t } = useTranslation();
  const [pngLoaded, setPngLoaded] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || isInViewport) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsInViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '250px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isInViewport]);

  const { pngUrl, gifUrl, isHovered, isLoading, handleMouseEnter, handleMouseLeave } = useImagePreview({
    modelId,
    solid,
    disableGif,
    enabled: isInViewport,
  });

  if (!isInViewport || isLoading) {
    return <div ref={containerRef} className="h-full w-full animate-pulse bg-gray-100" />;
  }

  if (!pngUrl) {
    return (
      <div ref={containerRef} className="flex h-full w-full items-center justify-center">
        <p className="m-0 text-sm text-gray-500">{t('models.noPreview')}</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      onMouseEnter={disableGif ? undefined : handleMouseEnter}
      onMouseLeave={disableGif ? undefined : handleMouseLeave}
    >
      {/* Pulse placeholder shown only until the PNG first loads */}
      {!pngLoaded && <div className="absolute inset-0 animate-pulse bg-gray-100" />}

      {/* Base layer: PNG — src is set once and never reassigned */}
      <img
        src={pngUrl}
        alt={alt}
        className={`absolute inset-0 w-full h-full object-contain transition-opacity ${pngLoaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setPngLoaded(true)}
        onError={() => setPngLoaded(true)}
        loading="lazy"
      />

      {/* Overlay layer: GIF — rendered only once gifUrl is available; src never changes.
          Visibility is controlled purely by CSS opacity on hover. */}
      {gifUrl && (
        <img
          src={gifUrl}
          alt={alt}
          aria-hidden={true}
          className={`absolute inset-0 w-full h-full object-contain transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}
          loading="lazy"
        />
      )}
    </div>
  );
};
